/**
 * ClinicPage, `/clinic` (podiatrist view).
 *
 * Slimmed surface (post owner-role retirement):
 *
 *  - Loads the user's primary clinic (resolved by AuthContext) via direct
 *    table reads. RLS (`clinics_select`) ensures the user only sees their
 *    own clinic.
 *  - Renders: clinic identity (name + address + contact + logo) only.
 *  - Empty/loading states throughout, never shows a forever-loading
 *    spinner.
 *
 * The Members, Email templates, and Follow-ups surfaces, and the
 * "Edit clinic" / "Invite member" header buttons, were removed because
 * the clinic 'owner' role and the clinic-edit permission model were
 * retired from the UI. They will return once that permission model is
 * rebuilt.
 */

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2, Globe, Mail, MapPin, Phone } from 'lucide-react';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Eyebrow,
  Heading,
  KeyValue,
  Skeleton,
} from '@insolech/ui';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/features/auth/AuthContext';
import { supabase } from '@/lib/supabase';

interface ClinicView {
  id: string;
  name: string;
  legal_name: string | null;
  address: string | null;
  city: string | null;
  emirate: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  website_url: string | null;
  logo_url: string | null;
  default_language: string;
}

export function ClinicPage() {
  const { t } = useTranslation('common');
  const { clinicId, clinics, profileLoading } = useAuth();

  // Multi-clinic selector state. AuthContext already pre-filters `clinics`
  // to active memberships (see PatientsListPage comment from 033f835), so
  // a simple `clinics.length > 1` check gates the dropdown.
  const [selectedClinicId, setSelectedClinicId] = React.useState<string | null>(
    clinicId ?? null,
  );

  // Keep selectedClinicId in sync with the user's available clinics. If the
  // current selection drops out of the membership list (e.g. profile
  // resolves after mount, or the user's default clinic changes), fall back
  // to the canonical `clinicId` from AuthContext.
  React.useEffect(() => {
    const stillValid =
      selectedClinicId !== null && clinics.some((c) => c.id === selectedClinicId);
    if (!stillValid) {
      setSelectedClinicId(clinicId ?? null);
    }
  }, [clinicId, clinics, selectedClinicId]);

  const clinicQuery = useQuery({
    queryKey: ['clinic', 'self', selectedClinicId],
    enabled: !!selectedClinicId,
    queryFn: async (): Promise<ClinicView | null> => {
      if (!selectedClinicId) return null;
      const { data, error } = await supabase
        .from('clinics')
        .select(
          'id, name, legal_name, address, city, emirate, country, phone, email, website_url, logo_url, default_language',
        )
        .eq('id', selectedClinicId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as ClinicView | null;
    },
  });

  // ────────── empty / loading guard ──────────
  if (profileLoading) {
    return (
      <AppShell>
        <main className="px-6 py-5 max-w-[1100px] mx-auto">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-72 mt-3" />
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardBody>
                <Skeleton className="h-32 w-full" />
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <Skeleton className="h-32 w-full" />
              </CardBody>
            </Card>
          </div>
        </main>
      </AppShell>
    );
  }

  if (clinics.length === 0 && !selectedClinicId) {
    return (
      <AppShell>
        <main className="px-6 py-5 max-w-[1100px] mx-auto">
          <Eyebrow>{t('nav.clinic', { defaultValue: 'Clinic' })}</Eyebrow>
          <Heading as="h1" size="h3" className="mt-2">
            Your clinic
          </Heading>
          <Card className="mt-6">
            <CardBody>
              <EmptyState
                icon={<Building2 className="h-6 w-6" />}
                title="Not yet linked to a clinic"
                description="Ask your admin for an invitation to join a clinic. Once linked, your clinic details will appear here."
              />
            </CardBody>
          </Card>
        </main>
      </AppShell>
    );
  }

  const clinic = clinicQuery.data;

  return (
    <AppShell>
      <main className="px-6 py-5 max-w-[1100px] mx-auto">
        {/* Multi-clinic selector (only when the user has 2+ active memberships) */}
        {clinics.length > 1 ? (
          <div className="mb-4 flex items-center gap-3">
            <label
              htmlFor="clinic-selector"
              className="text-sm font-medium text-text-secondary"
            >
              Viewing:
            </label>
            <select
              id="clinic-selector"
              value={selectedClinicId ?? ''}
              onChange={(e) => setSelectedClinicId(e.target.value || null)}
              className="h-9 rounded-md border border-border-default bg-surface-elevated px-3 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              {clinics.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div className="flex items-center gap-4 min-w-0">
            {clinic?.logo_url ? (
              <img
                src={clinic.logo_url}
                alt={`${clinic.name} logo`}
                className="h-14 w-14 rounded-md object-cover bg-sand-50 border border-border-subtle"
              />
            ) : (
              <div className="h-14 w-14 rounded-md bg-brand-50 text-brand-700 flex items-center justify-center border border-border-subtle">
                <Building2 className="h-6 w-6" />
              </div>
            )}
            <div className="min-w-0">
              <Eyebrow>{t('nav.clinic', { defaultValue: 'Clinic' })}</Eyebrow>
              <Heading as="h1" size="h3" className="mt-1 truncate">
                {clinicQuery.isLoading ? <Skeleton className="h-7 w-48" /> : clinic?.name ?? '-'}
              </Heading>
              {clinic?.legal_name ? (
                <p className="text-sm text-text-muted mt-0.5">{clinic.legal_name}</p>
              ) : null}
            </div>
          </div>
        </div>

        {clinicQuery.isError ? (
          <Card className="border-error-200">
            <CardBody>
              <p className="text-sm text-error-700">
                Could not load clinic details: {String(clinicQuery.error)}
              </p>
            </CardBody>
          </Card>
        ) : null}

        <div className="grid grid-cols-1 gap-6">
          {/* Identity + contact */}
          <Card>
            <CardHeader>
              <CardTitle>Clinic details</CardTitle>
              <CardDescription>
                Address, contact, and default language for outgoing communications.
              </CardDescription>
            </CardHeader>
            <CardBody>
              {clinicQuery.isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-5 w-1/2" />
                  <Skeleton className="h-5 w-2/3" />
                </div>
              ) : !clinic ? (
                <p className="text-sm text-text-muted">No clinic data available.</p>
              ) : (
                <KeyValue
                  items={[
                    {
                      label: (
                        <span className="inline-flex items-center gap-2">
                          <MapPin className="h-3.5 w-3.5" />
                          Address
                        </span>
                      ),
                      value: formatAddress(clinic),
                    },
                    {
                      label: (
                        <span className="inline-flex items-center gap-2">
                          <Phone className="h-3.5 w-3.5" />
                          Phone
                        </span>
                      ),
                      value: clinic.phone ?? '-',
                    },
                    {
                      label: (
                        <span className="inline-flex items-center gap-2">
                          <Mail className="h-3.5 w-3.5" />
                          Email
                        </span>
                      ),
                      value: clinic.email ?? '-',
                    },
                    {
                      label: (
                        <span className="inline-flex items-center gap-2">
                          <Globe className="h-3.5 w-3.5" />
                          Website
                        </span>
                      ),
                      value: clinic.website_url ? (
                        <a
                          href={clinic.website_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-brand-700 hover:underline"
                        >
                          {clinic.website_url}
                        </a>
                      ) : (
                        '-'
                      ),
                    },
                    {
                      label: 'Default language',
                      value: clinic.default_language.toUpperCase(),
                    },
                  ]}
                />
              )}
            </CardBody>
          </Card>
        </div>
      </main>
    </AppShell>
  );
}

function formatAddress(c: ClinicView): string {
  const parts = [c.address, c.city, c.emirate, c.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '-';
}
